'use client';

import { type PropsWithChildren, useId } from 'react';

import theme from '@/theme';

import { SkeletonBase } from './styled';

type TBgColorVariant = 'dark' | 'light';

export interface IRectProps {
  bgColorVariant?: TBgColorVariant;
  rounded?: boolean;
  height?: number | string;
  width?: number | string;
}

export const Rect = ({ bgColorVariant = 'dark', ...props }: IRectProps) => {
  const colorVariants = {
    dark: {
      background: theme.skeletonDarkBackgroundColor,
      foreground: theme.skeletonDarkForegroundColor,
    },
    light: {
      background: theme.skeletonLightBackgroundColor,
      foreground: theme.skeletonLightForegroundColor,
    },
  };

  const { background, foreground } = colorVariants[bgColorVariant];
  const key = useId();

  return (
    <SkeletonBase
      background={background}
      foreground={foreground}
      height={props.height}
      key={key}
      rounded={props.rounded}
      width={props.width}
    />
  );
};

interface ContainerProps {
  display?: 'flex' | 'grid' | 'block';
  flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'initial';
  alignItems?: 'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline';
  gap?: number | string;
  margin?: string;
  marginBottom?: number | string;
  margingTop?: number | string;
  borderRadius?: number | string;
  padding?: string;
  flexWrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  gridTemplateRows?: string;
  gridTemplateColumns?: string;
  position?: 'relative' | 'absolute';
  flexGrow?: number;
  overflowY?: 'auto';
  zIndex?: number;
}

export const Container = ({
  display = 'flex',
  children,
  ...rest
}: PropsWithChildren<ContainerProps>) => (
  <div
    style={{
      width: '100%',
      display,
      ...rest,
    }}
  >
    {children}
  </div>
);

export const Skeleton = { Rect, Container };
